// Third-party
import React, { ReactElement, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';

// Types
import { tooltipPlacement } from '@/types/tooltip';
import { buildTooltipAriaLabel } from './utils/accessibility';
import { calculateSmartPlace, PLACEMENT } from './utils/placement';

interface Props {
  'aria-label'?: string;
  'data-dd-privacy'?: 'allow' | 'mask' | 'hidden' | 'mask-user-input';
  'data-private'?: boolean;
  children: ReactElement;
  className?: string;
  delayShow?: number;
  disabled?: boolean;
  effect?: 'float' | 'solid';
  icon?: string;
  list?: Array<any>;
  message?: string | ReactElement;
  offset?: Object;
  place?: tooltipPlacement;
  title?: string;
}

let tooltipId = 0;

const Tooltip = ({ children, className, delayShow, disabled, effect = 'solid', icon, list, message, place = PLACEMENT.TOP, title, ...rest }: Props) => {
  const id = `tooltip-${tooltipId++}`;
  const selector = `[data-for="${id}"]`;

  const [smartPlace, setSmartPlace] = useState<tooltipPlacement>(place);
  const [isInDataTable, setIsInDataTable] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasCalculated = useRef(false);

  // Passing in data-private={false} will still show in logrocket - use undefined instead.
  const dataPrivate = rest['data-private'] ? true : undefined;
  const dataDdPrivacy = rest['data-dd-privacy'] ? rest['data-dd-privacy'] : 'allow';

  // Calculate placement immediately when container is mounted and on mouseenter
  useEffect(() => {
    if (containerRef.current) {
      const dataTable = containerRef.current.closest('.tst-data-table, .data-table, table');
      setIsInDataTable(!!dataTable);
    }

    // Initial calculation on mount
    if (containerRef.current && !hasCalculated.current) {
      const triggerElement = containerRef.current.querySelector(selector);
      if (triggerElement) {
        setSmartPlace(calculateSmartPlace(triggerElement, place));
        hasCalculated.current = true;
      }
    }

    // Dynamic calculation on mouseenter
    const handleShow = (event: any) => {
      if (event.target instanceof Element) {
        const target = event.target.closest(selector);
        if (target) {
          setSmartPlace(calculateSmartPlace(target, place));
        }
      }
    };

    document.addEventListener('mouseenter', handleShow, true);
    return () => {
      document.removeEventListener('mouseenter', handleShow, true);
    };
  }, [selector, place]);

  const tooltipContent = !disabled ? (
    <ReactTooltip
      id={id}
      effect={effect}
      event="focus mouseenter"
      eventOff="blur mouseleave"
      role="tooltip"
      aria-hidden="true"
      globalEventOff="keydown"
      place={smartPlace}
      className={classNames('custom-tooltip', className)}
      {...rest}
    >
      {title && (
        <h3>
          {icon && <i className={icon} />}
          <span>{title}</span>
        </h3>
      )}

      {message && <p className="tooltip-message">{message}</p>}

      {list && (
        <ul className="disclaimer-list">
          {list.map((item, i) => (
            <li key={i}>{item && item.toString()}</li>
          ))}
        </ul>
      )}
    </ReactTooltip>
  ) : null;

  return (
    // Can't use 'tooltip' class unfortunately, as that conflicts with bootstrap's styles.
    <div ref={containerRef} className={classNames('custom-tooltip', className)} data-private={dataPrivate} data-dd-privacy={dataDdPrivacy}>
      {React.Children.map(children, child => {
        const extraProps: any = {
          'data-tip': true,
          'data-for': id,
          'data-delay-show': delayShow,
        };

        // To ensure accessibility, we add an aria-label that includes the title, message, and list items because tooltips are not always read by screen readers.
        if (!child.props['aria-label']) {
          extraProps['aria-label'] = rest['aria-label'] ?? buildTooltipAriaLabel(title, message, list);
        }
        return React.cloneElement(child, extraProps);
      })}

      {/* If inside data table, render tooltip in portal to escape z-index constraints, otherwise render normally */}
      {isInDataTable && tooltipContent ? createPortal(tooltipContent, document.body) : tooltipContent}
    </div>
  );
};

export default Tooltip;
